// components/Login.tsx — the auth state machine for Ember Hollow.
//
// Implements AUTH_CONTRACT §1 exactly:
//   email ── send code ──▶ code ── verify ──▶ {setup → passkey} or game
//                                         │
//                                         └▶ recoveryRegistration → passkey → game
//
// Plus a "Sign in with passkey" landing-button that jumps straight into
// the passkey authentication ceremony (no email).

import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { styles } from "../styles";
import {
  isWebAuthnSupported,
  registerPasskey,
  supportsConditionalUI,
  authenticateWithAutofill,
} from "../services/passkey";
import {
  emailSend,
  emailVerify,
  setupAccount,
  passkeyAuthenticateOptions,
  passkeyVerifyAuthenticate,
  passkeyVerifyRegister,
  passkeyRegisterOptions,
  commitAuth,
  oauthGoogle,
  oauthApple,
  type SetupAccountResult,
} from "../services/authApi";
import { initGoogleSignIn, renderGoogleButton } from "../services/googleAuth";
import {
  initAppleSignIn,
  signInWithApple,
  AppleSignInError,
  APPLE_CANCEL_CODES,
} from "../services/appleAuth";
import type { AuthUser } from "../auth/tokens";
import LandingPane from "./LandingPane";
import LandingOverlay from "./LandingOverlay";
import ScenarioDropdown from "./ScenarioDropdown";
import { useScenario } from "../scenarios/ScenarioContext";
import type { ScenarioTheme } from "../scenarios/presentation";

// Two-column landing kicks in at this viewport width. Below this we stack:
// marketing on top, auth below. Inline styles can't express media queries,
// so we read the viewport from a resize listener.
const TWO_COLUMN_BREAKPOINT = 860;

function useViewportWidth(): number {
  const [width, setWidth] = useState<number>(() =>
    typeof window === "undefined" ? 1024 : window.innerWidth
  );
  useEffect(() => {
    function onResize() {
      setWidth(window.innerWidth);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
}

type Step = "email" | "code" | "setup" | "passkey-create";

interface RecoveryRegistration {
  challengeId: string;
  options: Parameters<typeof registerPasskey>[0];
  user: AuthUser;
  token: string;
  refreshToken: string;
}

interface PostSetupRegistration {
  // Setup-account already issued real session tokens for the new user; we
  // attach the registration ceremony required by the contract.
  challengeId: string;
  options: Parameters<typeof registerPasskey>[0];
  user: AuthUser;
  token: string;
  refreshToken: string;
}

interface LoginProps {
  onSignedIn: (user: AuthUser) => void;
  /**
   * When true on mount, the LandingOverlay opens immediately. Used by the
   * `?policy=1` deep-link from external ad creatives. App.tsx reads the
   * query param, strips it, and forwards the flag here.
   */
  initialOverlayOpen?: boolean;
  /**
   * Optional anchor id (without `#`) inside the overlay to scroll to once
   * it opens. Forwarded straight to LandingOverlay's `initialScrollTo`.
   */
  initialOverlayScrollTo?: string;
}

export default function Login({
  onSignedIn,
  initialOverlayOpen = false,
  initialOverlayScrollTo,
}: LoginProps) {
  const { t } = useTranslation(["auth", "common", "scenarios"]);
  // ── Feature detection ──────────────────────────────────────────────────
  const [passkeySupported, setPasskeySupported] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const supported = isWebAuthnSupported();
      if (cancelled) return;
      setPasskeySupported(supported);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Flow state ─────────────────────────────────────────────────────────
  const [step, setStep] = useState<Step>("email");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when the user picks a passkey from the autofill dropdown and the
  // verification round-trip is in progress. Drives the spinner on the
  // "Continue with email" button so the user sees feedback immediately.
  const [conditionalUIBusy, setConditionalUIBusy] = useState(false);

  // Email/code
  const [email, setEmail] = useState("");
  const [codeId, setCodeId] = useState<string | null>(null);
  const [code, setCode] = useState("");

  // New-user setup
  const [setupToken, setSetupToken] = useState<string | null>(null);
  const [name, setName] = useState("");

  // Passkey ceremony state
  const [pendingRegistration, setPendingRegistration] = useState<
    PostSetupRegistration | RecoveryRegistration | null
  >(null);
  const [isRecoveryFlow, setIsRecoveryFlow] = useState(false);
  const [canSkipPasskey, setCanSkipPasskey] = useState(false);
  const [isPasskeyNag, setIsPasskeyNag] = useState(false);

  // ── Helpers ────────────────────────────────────────────────────────────

  function reset(toStep: Step = "email") {
    setStep(toStep);
    setError(null);
    setCode("");
    setCodeId(null);
    setSetupToken(null);
    setName("");
    setPendingRegistration(null);
    setIsRecoveryFlow(false);
    setCanSkipPasskey(false);
    setIsPasskeyNag(false);
  }

  function finalize(user: AuthUser) {
    onSignedIn(user);
  }

  // ── OAuth handlers ─────────────────────────────────────────────────────

  async function handleGoogleSignIn(credential: string) {
    setError(null);
    setBusy(true);
    try {
      const result = await oauthGoogle(credential, effective.id);
      if (result.user.foreverDefaultScenarioId) {
        setSelected(result.user.foreverDefaultScenarioId);
      }
      commitAuth(result.token, result.refreshToken, result.user);
      finalize(result.user);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("auth:oauth.googleFailed"));
    } finally {
      setBusy(false);
    }
  }

  async function handleAppleSignIn() {
    setError(null);
    setBusy(true);
    try {
      const clientId = import.meta.env.VITE_APPLE_CLIENT_ID;
      if (clientId) {
        await initAppleSignIn(clientId, window.location.origin);
      }
      const appleResult = await signInWithApple();
      const result = await oauthApple(appleResult.id_token, appleResult.user, effective.id);
      if (result.user.foreverDefaultScenarioId) {
        setSelected(result.user.foreverDefaultScenarioId);
      }
      commitAuth(result.token, result.refreshToken, result.user);
      finalize(result.user);
    } catch (err: unknown) {
      // User-driven cancels (closed popup, declined) are silent no-ops.
      if (err instanceof AppleSignInError && APPLE_CANCEL_CODES.has(err.code)) {
        setBusy(false);
        return;
      }
      // Log Apple's real code for diagnosis (e.g. a Services ID / Return-URL
      // mismatch surfaces here), but show the user a friendly message rather
      // than a raw code or "[object Object]".
      if (err instanceof AppleSignInError) {
        console.error("[apple-signin] failed:", err.code);
      } else {
        console.error("[apple-signin] failed:", err);
      }
      setError(t("auth:oauth.appleFailed"));
    } finally {
      setBusy(false);
    }
  }

  // ── Step 1: Send email code ────────────────────────────────────────────

  async function handleSendCode(e?: FormEvent) {
    if (e) e.preventDefault();
    const cleaned = email.trim();
    if (!cleaned) return;
    setError(null);
    setBusy(true);
    try {
      const { codeId: id } = await emailSend(cleaned);
      setCodeId(id);
      setStep("code");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("auth:code.sendFailed"));
    } finally {
      setBusy(false);
    }
  }

  // ── Step 2: Verify code ────────────────────────────────────────────────

  async function handleVerifyCode(e?: FormEvent) {
    if (e) e.preventDefault();
    if (!codeId || code.length < 6) return;
    setError(null);
    setBusy(true);
    try {
      const result = await emailVerify(codeId, code.trim());

      if (result.isNewUser) {
        // New user: hold the setup token; collect name; setup-account; then register passkey.
        setSetupToken(result.token);
        setStep("setup");
        setBusy(false);
        return;
      }

      // Existing user — snap the login surface to their locked scenario
      // before we transition. Applies to both sub-branches (hasPasskeys
      // signin + recovery passkey-create). setSelected is a noop on
      // unknown ids so an old web bundle pointed at a newer server doesn't
      // crash the surface.
      if (result.user.foreverDefaultScenarioId) {
        setSelected(result.user.foreverDefaultScenarioId);
      }

      if (!result.hasPasskeys) {
        // Recovery: must register a passkey before we can complete sign-in.
        if (!result.recoveryRegistration) {
          // Server contract violation — surface a friendly error.
          setError(t("auth:setup.missingPasskeyData"));
          setBusy(false);
          return;
        }
        if (!passkeySupported) {
          // Browser can't do passkeys — skip straight to game.
          commitAuth(result.token, result.refreshToken, result.user);
          finalize(result.user);
          setBusy(false);
          return;
        }
        setPendingRegistration({
          challengeId: result.recoveryRegistration.challengeId,
          options: result.recoveryRegistration.options,
          user: result.user,
          token: result.token,
          refreshToken: result.refreshToken,
        });
        setIsRecoveryFlow(true);
        setCanSkipPasskey(true);
        setIsPasskeyNag(result.user.emailLoginCount >= 3 && result.user.passkeyCount === 0);
        setStep("passkey-create");
        setBusy(false);
        return;
      }

      // Existing user with passkeys: tokens are valid sign-in already.
      commitAuth(result.token, result.refreshToken, result.user);
      finalize(result.user);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("auth:code.invalidCode"));
      setBusy(false);
    }
  }

  async function handleResendCode() {
    if (!email.trim() || busy) return;
    setError(null);
    setBusy(true);
    try {
      const { codeId: id } = await emailSend(email.trim());
      setCodeId(id);
      setCode("");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("auth:code.resendFailed"));
    } finally {
      setBusy(false);
    }
  }

  function handleChangeEmail() {
    setStep("email");
    setCode("");
    setCodeId(null);
    setError(null);
  }

  // ── Step 3: Setup account (new user) ───────────────────────────────────

  async function handleSetup(e: FormEvent) {
    e.preventDefault();
    if (!setupToken || !name.trim()) return;
    setError(null);
    setBusy(true);
    try {
      // Carry the pill selection through to setup-account so the server
      // pins both the user's first story scenarioId AND their
      // forever_default_scenario_id in the same transaction. Pre-this,
      // setup-account hardcoded ember_hollow regardless of what the user
      // had picked on the login surface — a Vaultsworn fan would sign up
      // and land in Ember Hollow. See AUTH_CONTRACT.md setup-account.
      const result: SetupAccountResult = await setupAccount(
        setupToken,
        name.trim(),
        effective.id,
      );
      // We hold the new session tokens but DO NOT persist them yet — the
      // contract requires the passkey ceremony to succeed first. We pass
      // them to the ceremony step and only commitAuth when register verify
      // returns ok.
      setPendingRegistration({
        challengeId: result.registration.challengeId,
        options: result.registration.options,
        user: result.user,
        token: result.token,
        refreshToken: result.refreshToken,
      });
      setCanSkipPasskey(true);
      setIsPasskeyNag(false);
      setIsRecoveryFlow(false);
      setStep("passkey-create");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t("auth:setup.createFailed"));
    } finally {
      setBusy(false);
    }
  }

  // ── Step 4: Passkey registration ceremony ──────────────────────────────

  // Track whether we've already attempted (and failed) a passkey ceremony so
  // the retry path knows to fetch fresh registration options — the original
  // challenge is single-use and was consumed by the first attempt.
  const [passkeyAttempted, setPasskeyAttempted] = useState(false);

  async function handleCreatePasskey() {
    if (!pendingRegistration) return;
    setError(null);
    setBusy(true);
    try {
      let { challengeId, options } = pendingRegistration;

      // On retry after a failed attempt, the original challenge was consumed
      // by the server (single-use). Fetch fresh options so the new ceremony
      // gets a valid challenge. The user's session token is still good (24h).
      if (passkeyAttempted) {
        const fresh = await passkeyRegisterOptions(pendingRegistration.token);
        challengeId = fresh.challengeId;
        options = fresh.options;
        setPendingRegistration((prev) =>
          prev ? { ...prev, challengeId, options } : prev,
        );
      }

      const credential = await registerPasskey(options);
      setPasskeyAttempted(true);
      // Register-mode verify needs bearer auth. Use the session token we got
      // from setup-account / email-verify (recovery) — not an existing token.
      await passkeyVerifyRegister(
        challengeId,
        credential,
        pendingRegistration.token
      );
      // Server returns { ok, credentialId }; our session tokens are still the
      // ones we captured before. Persist + finalize.
      commitAuth(
        pendingRegistration.token,
        pendingRegistration.refreshToken,
        pendingRegistration.user
      );
      finalize(pendingRegistration.user);
    } catch (err: unknown) {
      setPasskeyAttempted(true);
      setError(err instanceof Error ? err.message : t("auth:passkey.createPasskeyFailed"));
      // Stay on the passkey-create step so the user can retry. Don't lose state.
    } finally {
      setBusy(false);
    }
  }

  // ── Skip passkey (dismissible) ──────────────────────────────────────────

  function handleSkipPasskey() {
    if (!pendingRegistration) return;
    commitAuth(
      pendingRegistration.token,
      pendingRegistration.refreshToken,
      pendingRegistration.user,
    );
    finalize(pendingRegistration.user);
  }

  // ── Render ─────────────────────────────────────────────────────────────

  const viewportWidth = useViewportWidth();
  const isTwoColumn = viewportWidth >= TWO_COLUMN_BREAKPOINT;
  // Overlay open-state. Seeded from the `?policy=1` deep-link prop so an
  // ad visitor lands with the overlay already up; we only consume the
  // initial scroll target on that first open (see LandingOverlay below).
  const [overlayOpen, setOverlayOpen] = useState(initialOverlayOpen);
  // Scroll target for the overlay. Set from the deep-link prop on first
  // mount; cleared when the user closes the overlay so a subsequent open
  // via the "About" button lands at the top of the overlay (the normal
  // scenario About title), not at the policy block.
  const [overlayScrollTo, setOverlayScrollTo] = useState<string | undefined>(
    initialOverlayScrollTo,
  );
  const handleOverlayClose = (): void => {
    setOverlayOpen(false);
    setOverlayScrollTo(undefined);
  };

  // Scenario-themed presentation (hero copy, "About" copy, theme vars).
  // Reads from the ScenarioProvider that App.tsx wraps the !authed branch in.
  //
  // `effective` drives the theme; `setSelected` lets us snap to a known
  // user's locked scenario the moment we identify them (recovery flow +
  // direct passkey signin both call it before the App flips into Game).
  const { effective, setSelected } = useScenario();

  // ── Conditional UI (passkey autofill) ──────────────────────────────────
  // On mount, register a non-blocking passkey listener on the email input.
  // If the user picks a passkey from the browser's autofill dropdown, we
  // verify it and sign them in — no email entry needed.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const supported = await supportsConditionalUI();
      if (cancelled || !supported) return;
      try {
        const { challengeId, options } = await passkeyAuthenticateOptions();
        if (cancelled) return;
        const credential = await authenticateWithAutofill(options);
        if (cancelled) return;
        setConditionalUIBusy(true);
        const result = await passkeyVerifyAuthenticate(challengeId, credential);
        if (cancelled) return;
        if (result.user.foreverDefaultScenarioId) {
          setSelected(result.user.foreverDefaultScenarioId);
        }
        commitAuth(result.token, result.refreshToken, result.user);
        finalize(result.user);
      } catch {
        // User didn't pick a passkey, or ceremony failed — silent no-op.
        if (!cancelled) setConditionalUIBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Resolve scenario-themed display strings via the i18n bundle. Falls back
  // to the structural `pillLabel` if the bundle is mid-load.
  const heroTitle = t(`scenarios:${effective.id}.heroTitle`, {
    defaultValue: effective.pillLabel,
  });
  const heroSubtitle = t(`scenarios:${effective.id}.heroSubtitle`, {
    defaultValue: "",
  });

  // Marketing pane is shown only on the email (landing) step. Once an auth
  // flow is in progress, hide it and recentre the auth card.
  const showMarketing = step === "email";

  // When the marketing pane is rendered alongside the auth card on the
  // email step, the marketing pane already carries the scenario title —
  // duplicating it inside the auth card competes with itself, so we
  // suppress the auth-card hero in that case. On every other step the
  // auth card is centred and stands alone, so the title + pill row return.
  const showAuthCardHero = !showMarketing;

  // Hide the scenario pills from the moment any commitment-to-account
  // step is active. New users: setup → passkey-create. Existing recovery
  // users: passkey-create. In both cases the user's scenario is already
  // determined (by what they picked, or by their forever-default the
  // server identified) and a visibly-theme-swapping pill row is a
  // distraction. Returning users with passkeys never see passkey-create
  // at all so they're unaffected.
  const showScenarioPills =
    showAuthCardHero &&
    step !== "setup" &&
    step !== "passkey-create";

  const authCard = (
    <div style={styles.loginCard}>
      {showAuthCardHero && (
        <>
          {showScenarioPills ? (
            <ScenarioDropdown variant="hero" />
          ) : (
            <h1 style={styles.loginTitle}>{heroTitle}</h1>
          )}
          <div style={styles.loginSub}>{heroSubtitle}</div>
        </>
      )}

      {error && (
        <div style={styles.loginErr} role="alert">
          {error}
        </div>
      )}

      {step === "email" && (
        <EmailStep
          email={email}
          setEmail={setEmail}
          busy={busy}
          conditionalUIBusy={conditionalUIBusy}
          onSendCode={handleSendCode}
          onGoogleSignIn={handleGoogleSignIn}
          onAppleSignIn={handleAppleSignIn}
        />
      )}

      {step === "code" && (
        <CodeStep
          email={email}
          code={code}
          setCode={setCode}
          busy={busy}
          onVerify={handleVerifyCode}
          onResend={handleResendCode}
          onChangeEmail={handleChangeEmail}
        />
      )}

      {step === "setup" && (
        <SetupStep
          email={email}
          name={name}
          setName={setName}
          busy={busy}
          passkeySupported={passkeySupported}
          onSubmit={handleSetup}
          onBack={() => reset("email")}
        />
      )}

      {step === "passkey-create" && (
        <PasskeyCreateStep
          isRecovery={isRecoveryFlow}
          busy={busy}
          canSkip={canSkipPasskey}
          isNag={isPasskeyNag}
          onCreate={handleCreatePasskey}
          onSkip={handleSkipPasskey}
          onBack={() => reset("email")}
        />
      )}

    </div>
  );

  // Layout:
  // - showMarketing && isTwoColumn:  marketing | auth, side by side.
  // - showMarketing && !isTwoColumn: marketing on top, auth below (stacked).
  // - !showMarketing: just the auth card, centred (mid-flow).
  let body: JSX.Element;
  if (!showMarketing) {
    body = authCard;
  } else if (isTwoColumn) {
    body = (
      <div style={styles.loginLayout}>
        <LandingPane
          onLearnMore={() => setOverlayOpen(true)}
          overlayId="ember-landing-overlay"
        />
        <div style={styles.loginAuthSide}>{authCard}</div>
      </div>
    );
  } else {
    body = (
      <div style={styles.loginLayoutStacked}>
        <LandingPane
          onLearnMore={() => setOverlayOpen(true)}
          stacked
          overlayId="ember-landing-overlay"
        />
        {authCard}
      </div>
    );
  }

  // Theme variables — applied as CSS custom properties on the outer
  // wrapper. Every login-screen style reads from these vars, so the entire
  // surface recolours together when `effective` changes.
  const themeVars = themeVarsFor(effective.theme);

  return (
    <div style={{ ...themeVars, ...styles.loginRoot }} className="ember-login-root">
      <div style={styles.loginShell}>
        <div style={styles.grain} />
        {body}

        <LandingOverlay
          open={overlayOpen}
          onClose={handleOverlayClose}
          id="ember-landing-overlay"
          initialScrollTo={overlayScrollTo}
        />

        <style>{`
          @keyframes flicker { 0%,100%{opacity:1} 50%{opacity:0.92} }
          @keyframes pulse { 0%,100%{transform:scale(1);opacity:0.4} 50%{transform:scale(1.4);opacity:1} }
          @keyframes fadein { from{opacity:0;transform:translateY(4px)} to{opacity:1;transform:translateY(0)} }
          @keyframes spin { to { transform: rotate(360deg); } }
          @media (prefers-reduced-motion: reduce) {
            .ember-login-root,
            .ember-login-root * {
              transition-duration: 0.001ms !important;
              animation-duration: 0.001ms !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
}

// Map a ScenarioTheme into a CSSProperties object containing CSS custom
// properties. React allows arbitrary string keys on CSSProperties as long
// as we cast — TS's CSSProperties type doesn't model `--foo` keys.
function themeVarsFor(theme: ScenarioTheme): CSSProperties {
  return {
    ["--bg-gradient" as keyof CSSProperties]: theme.bgGradient,
    ["--accent" as keyof CSSProperties]: theme.accent,
    ["--accent-muted" as keyof CSSProperties]: theme.accentMuted,
    ["--ink" as keyof CSSProperties]: theme.ink,
    ["--ink-muted" as keyof CSSProperties]: theme.inkMuted,
    ["--paper" as keyof CSSProperties]: theme.paper,
    ["--paper-edge" as keyof CSSProperties]: theme.paperEdge,
    ["--font-body" as keyof CSSProperties]: theme.fontFamily,
  } as CSSProperties;
}


// ── Step components ──────────────────────────────────────────────────────

function EmailStep(props: {
  email: string;
  setEmail: (v: string) => void;
  busy: boolean;
  conditionalUIBusy: boolean;
  onSendCode: (e?: FormEvent) => void;
  onGoogleSignIn: (credential: string) => void;
  onAppleSignIn: () => void;
}) {
  const {
    email,
    setEmail,
    busy,
    conditionalUIBusy,
    onSendCode,
    onGoogleSignIn,
    onAppleSignIn,
  } = props;
  const { t } = useTranslation(["auth", "common"]);

  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;
  const appleClientId = import.meta.env.VITE_APPLE_CLIENT_ID as string | undefined;
  const hasOAuth = !!googleClientId || !!appleClientId;

  const googleBtnRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!googleClientId || !googleBtnRef.current) return;
    let cancelled = false;
    (async () => {
      await initGoogleSignIn(googleClientId, onGoogleSignIn);
      if (!cancelled && googleBtnRef.current) {
        renderGoogleButton(googleBtnRef.current);
      }
    })();
    return () => { cancelled = true; };
  }, [googleClientId]);

  return (
    <>
      {appleClientId && (
        <button
          type="button"
          style={{
            ...styles.loginPrimary,
            backgroundColor: "#000",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            marginBottom: 0,
          }}
          onClick={onAppleSignIn}
          disabled={busy}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 256 315"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ flexShrink: 0 }}
          >
            <path
              d="M213.803 167.03c.442 47.58 41.74 63.413 42.197 63.615-.347 1.116-6.597 22.563-21.757 44.716-13.104 19.153-26.705 38.235-48.13 38.63-21.05.388-27.82-12.483-51.888-12.483-24.061 0-31.582 12.088-51.51 12.871-20.68.783-36.428-20.71-49.64-39.793C7.228 235.418-18.79 152.665 8.475 98.186c13.504-27.03 37.655-44.168 63.884-44.56 20.316-.388 39.487 13.652 51.889 13.652 12.39 0 35.652-16.881 60.094-14.401 10.236.427 38.952 4.136 57.379 31.147-1.486.922-34.252 19.998-33.918 59.706m-39.576-116.689C185.878 36.067 192.401 17.678 190.57 0c-15.757.641-34.844 10.507-46.137 23.73-10.127 11.762-18.99 30.587-16.61 48.617 17.576 1.365 35.528-8.941 46.404-21.306"
              fill="#fff"
            />
          </svg>
          {t("auth:oauth.signInWithApple")}
        </button>
      )}

      {googleClientId && (
        <div ref={googleBtnRef} style={{ marginTop: 12, minHeight: 44 }} />
      )}

      {hasOAuth && (
        <div style={styles.loginDivider}>
          <div style={styles.loginDividerLine} />
          <span>{t("auth:oauth.or")}</span>
          <div style={styles.loginDividerLine} />
        </div>
      )}

      <form onSubmit={onSendCode}>
        <div style={styles.loginFormGroup}>
          <label style={styles.loginFieldLabel} htmlFor="ember-email">
            {t("auth:email.label")}
          </label>
          <input
            id="ember-email"
            type="email"
            inputMode="email"
            autoComplete="username webauthn"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            placeholder={t("auth:email.placeholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy || conditionalUIBusy}
            required
            autoFocus
            style={styles.loginField}
          />
        </div>
        <button
          type="submit"
          style={btnStyle(styles.loginSecondary, busy || conditionalUIBusy || !email.trim())}
          disabled={busy || conditionalUIBusy || !email.trim()}
        >
          {busy || conditionalUIBusy ? <span style={styles.loginSpinner} aria-hidden /> : null}
          {conditionalUIBusy ? t("auth:oauth.signingIn") : t("auth:email.continueWithEmail")}
        </button>
      </form>
    </>
  );
}

function CodeStep(props: {
  email: string;
  code: string;
  setCode: (v: string) => void;
  busy: boolean;
  onVerify: (e?: FormEvent) => void;
  onResend: () => void;
  onChangeEmail: () => void;
}) {
  const { email, code, setCode, busy, onVerify, onResend, onChangeEmail } = props;
  const { t } = useTranslation("auth");
  // The "We sent a code to <em>{email}</em>." line interpolates the email
  // into an italicised accent span. The bundle uses <emph> as the tag.
  const bodyTemplate = t("code.body", { email });
  const [bodyBefore, bodyAfter] = splitOnTag(bodyTemplate, "emph", email);
  return (
    <>
      <div style={styles.loginStepLabel}>{t("code.eyebrow")}</div>
      <h2 style={styles.loginHeading}>{t("code.heading")}</h2>
      <p style={styles.loginBody}>
        {bodyBefore}
        <em style={{ color: "var(--accent)" }}>{email}</em>
        {bodyAfter}
      </p>

      <form onSubmit={onVerify}>
        <div style={styles.loginFormGroup}>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder={t("code.placeholder")}
            value={code}
            onChange={(e) =>
              setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            disabled={busy}
            required
            autoFocus
            maxLength={6}
            style={styles.loginCodeField}
          />
        </div>

        <button
          type="submit"
          style={btnStyle(styles.loginPrimary, busy || code.length < 6)}
          disabled={busy || code.length < 6}
        >
          {busy ? <span style={styles.loginSpinner} aria-hidden /> : null}
          {t("code.verifyCta")}
        </button>
      </form>

      <div style={styles.loginRow}>
        <button
          type="button"
          style={styles.loginLink}
          onClick={onResend}
          disabled={busy}
        >
          {t("code.resend")}
        </button>
        <span style={{ color: "var(--ink-muted)", opacity: 0.6 }}>·</span>
        <button
          type="button"
          style={styles.loginLink}
          onClick={onChangeEmail}
          disabled={busy}
        >
          {t("code.changeEmail")}
        </button>
      </div>
    </>
  );
}

/**
 * Helper for split-rendering a translated string that contains a single
 * `<emph>{{value}}</emph>` style tag (used so the email / setup body lines
 * can render the interpolated value in an italic accent span without
 * embedding raw HTML through the translation system).
 *
 * Returns the [before, after] text fragments that surround the tagged
 * span. If the tag is missing (translator dropped it), returns the whole
 * translated string in `before` so the email at least renders inline.
 */
function splitOnTag(
  translated: string,
  tag: string,
  _value: string,
): [string, string] {
  const open = `<${tag}>`;
  const close = `</${tag}>`;
  const a = translated.indexOf(open);
  if (a < 0) return [translated, ""];
  const b = translated.indexOf(close, a + open.length);
  if (b < 0) return [translated, ""];
  const before = translated.slice(0, a);
  const after = translated.slice(b + close.length);
  return [before, after];
}

function SetupStep(props: {
  email: string;
  name: string;
  setName: (v: string) => void;
  busy: boolean;
  passkeySupported: boolean;
  onSubmit: (e: FormEvent) => void;
  onBack: () => void;
}) {
  const { email, name, setName, busy, passkeySupported, onSubmit, onBack } = props;
  const { t } = useTranslation(["auth", "common"]);
  const bodyTemplate = t("auth:setup.body", { email });
  const [bodyBefore, bodyAfter] = splitOnTag(bodyTemplate, "emph", email);

  return (
    <>
      <div style={styles.loginStepLabel}>{t("auth:setup.eyebrow")}</div>
      <h2 style={styles.loginHeading}>{t("auth:setup.heading")}</h2>
      <p style={styles.loginBody}>
        {bodyBefore}
        <em style={{ color: "var(--accent)" }}>{email}</em>
        {bodyAfter}
      </p>

      {!passkeySupported && (
        <div style={styles.loginInfo}>
          {t("auth:setup.noPasskeyNote")}
        </div>
      )}

      <form onSubmit={onSubmit}>
        <div style={styles.loginFormGroup}>
          <label style={styles.loginFieldLabel} htmlFor="ember-name">
            {t("auth:setup.nameLabel")}
          </label>
          <input
            id="ember-name"
            type="text"
            autoComplete="name"
            autoCapitalize="words"
            autoCorrect="off"
            placeholder={t("auth:setup.namePlaceholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={busy}
            required
            autoFocus
            style={styles.loginField}
          />
        </div>

        <button
          type="submit"
          style={btnStyle(
            styles.loginPrimary,
            busy || !name.trim()
          )}
          disabled={busy || !name.trim()}
        >
          {busy ? <span style={styles.loginSpinner} aria-hidden /> : null}
          {t("common:buttons.continue")}
        </button>
      </form>

      <div style={styles.loginRow}>
        <button
          type="button"
          style={styles.loginLink}
          onClick={onBack}
          disabled={busy}
        >
          {t("common:buttons.back")}
        </button>
      </div>
    </>
  );
}

function PasskeyCreateStep(props: {
  isRecovery: boolean;
  busy: boolean;
  canSkip: boolean;
  isNag: boolean;
  onCreate: () => void;
  onSkip: () => void;
  onBack: () => void;
}) {
  const { isRecovery, busy, canSkip, isNag, onCreate, onSkip, onBack } = props;
  const { t } = useTranslation(["auth", "common"]);

  // Nag variant uses distinct copy to explain why the user should add a passkey.
  const eyebrow = isNag
    ? t("auth:passkey.nagEyebrow")
    : isRecovery
      ? t("auth:passkey.createPasskeyRecoveryEyebrow")
      : t("auth:passkey.createPasskeyEyebrow");
  const title = isNag
    ? t("auth:passkey.nagTitle")
    : isRecovery
      ? t("auth:passkey.createPasskeyRecoveryTitle")
      : t("auth:passkey.createPasskeyTitle");
  const body = isNag
    ? t("auth:passkey.nagBody")
    : isRecovery
      ? t("auth:passkey.createPasskeyRecoveryBody")
      : t("auth:passkey.createPasskeyBody");

  return (
    <>
      <div style={styles.loginStepLabel}>{eyebrow}</div>
      <h2 style={styles.loginHeading}>{title}</h2>
      <p style={styles.loginBody}>{body}</p>

      <button
        type="button"
        style={btnStyle(styles.loginPrimary, busy)}
        onClick={onCreate}
        disabled={busy}
      >
        {busy ? <span style={styles.loginSpinner} aria-hidden /> : null}
        {t("auth:passkey.createPasskeyCta")}
      </button>

      <div style={styles.loginRow}>
        {canSkip ? (
          <button
            type="button"
            style={styles.loginLink}
            onClick={onSkip}
            disabled={busy}
          >
            {isNag
              ? t("auth:passkey.remindMeNextTime")
              : t("auth:passkey.skipForNow")}
          </button>
        ) : (
          <button
            type="button"
            style={styles.loginLink}
            onClick={onBack}
            disabled={busy}
          >
            {t("common:buttons.cancel")}
          </button>
        )}
      </div>
    </>
  );
}

// Spread helper that respects disabled styling.
function btnStyle(base: CSSProperties, disabled: boolean): CSSProperties {
  return {
    ...base,
    opacity: disabled ? 0.45 : 1,
    cursor: disabled ? "default" : "pointer",
  };
}
